import React, { useState, useEffect } from "react";
import { useAuth } from "../../hooks/auth";
import api from "../../services/api";
import { getIsAdminFromRecord } from "../FullCalendar/FullCalendarUtils";
import {
  Box,
  Flex,
  VStack,
  Text,
  Link,
  Icon,
  Collapse,
  IconButton,
  Image,
} from "@chakra-ui/react";
import { NavLink, Link as RouterLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import {
  AiOutlineLineChart,
  AiOutlineDown,
  AiOutlineRight,
  AiOutlineMenu,
  AiOutlineClose,
  AiOutlineCalendar,
  AiOutlineUnorderedList,
  AiOutlineFileText,
  AiOutlineSetting,
  AiOutlineTeam,
  AiOutlineTags,
  AiOutlineEye,
} from "react-icons/ai";


const logoUrl =
  "https://s3-contel-imagens-aplicacao.s3.us-east-1.amazonaws.com/nogle/Brand.svg";

const commonFlexStyles = (isCollapsed) => ({
  align: "center",
  h: "48px",
  borderRadius: "md",
  cursor: "pointer",
  transition: "background-color .2s ease",
  _hover: { bg: "gray.600", color: "white" },
  w: "full",
  justifyContent: isCollapsed ? "center" : "flex-start",
  px: isCollapsed ? 2 : 4,
  whiteSpace: "nowrap",
});

const NavItem = ({ icon, children, isCollapsed, to = "#", ...rest }) => (
  <Link
    as={NavLink}
    to={to}
    end
    _hover={{ textDecoration: "none" }}
    w="full"
    _activeLink={{ color: "secondary" }}
  >
    {({ isActive }) => (
      <Flex
        {...commonFlexStyles(isCollapsed)}
        position="relative"
        {...rest}
        bg={isActive ? "gray.700" : "transparent"}
        color={isActive ? "secondary" : "white"}
      >
        <Box
          position="absolute"
          left="0"
          h="80%"
          w="2px"
          bg="secondary"
          borderRadius="full"
          opacity={isActive ? 1 : 0}
          transition="opacity 0.2s ease-in-out"
          top="50%"
          transform="translateY(-50%)"
        />

        {icon && <Icon fontSize="20px" as={icon} ml={isCollapsed ? 0 : 0} />}

        <AnimatePresence initial={false}>
          {!isCollapsed && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              style={{
                width: "100%",
                whiteSpace: "nowrap",
                marginLeft: "1rem",
                overflow: "hidden",
              }}
            >
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </Flex>
    )}
  </Link>
);

const Sidebar = ({ isCollapsed, onToggle }) => {
  const [reportMenuOpen, setReportMenuOpen] = useState(false);
  const [controlsMenuOpen, setControlsMenuOpen] = useState(false);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const currentTab = searchParams.get("tab");

  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const res = await api.get("/calendar/me");
        setIsAdmin(getIsAdminFromRecord(res.data));
      } catch {
        setIsAdmin(false);
      }
    };
    if (user) {
      checkAdmin();
    }
  }, [user]);

  const sidebarWidth = isCollapsed ? "80px" : "280px";

  return (
    <Box
      as={motion.div}
      animate={{ width: sidebarWidth }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      overflowX="hidden"
      bg="sidebarBg"
      borderRight="1px"
      borderColor="sidebarBorder"
      h="100vh"
      zIndex={10}
      flexShrink={0}
    >
      <Flex
        h="20"
        alignItems="center"
        px={isCollapsed ? 2 : 4}
        justifyContent={isCollapsed ? "center" : "space-between"}
      >
        <AnimatePresence initial={false}>
          {!isCollapsed && (
            <motion.div
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: "100px" }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
            >
              <RouterLink to="/">
                <Image
                  src={logoUrl}
                  alt="Nogle Logo"
                  w="100px"
                  cursor="pointer"
                />
              </RouterLink>
            </motion.div>
          )}
        </AnimatePresence>
        <IconButton
          aria-label="Toggle Menu"
          icon={isCollapsed ? <AiOutlineMenu /> : <AiOutlineClose />}
          variant="ghost"
          onClick={onToggle}
          fontSize="xl"
          _hover={{ bg: "gray.600", color: "secondary" }}
          color="secondary"
        />
      </Flex>

      <VStack spacing={1} align="stretch" as="nav" px={isCollapsed ? 2 : 4}>
        <NavItem
          icon={AiOutlineCalendar}
          isCollapsed={isCollapsed}
          to="/calendar"
        >
          <Text>Agenda</Text>
        </NavItem>

        <NavItem
          icon={AiOutlineUnorderedList}
          isCollapsed={isCollapsed}
          to="/meus-eventos"
        >
          <Text>Meus eventos</Text>
        </NavItem>

        {isAdmin && (
          <Box>
            <Flex
              align="center"
              h="48px"
              px={4}
              borderRadius="md"
              cursor="pointer"
              _hover={{ bg: "gray.600" }}
              color="white"
              onClick={() => {
                if (!isCollapsed) setReportMenuOpen(!reportMenuOpen);
              }}
              justifyContent={isCollapsed ? "center" : "flex-start"}
            >
              <Icon fontSize="20px" as={AiOutlineFileText} />
              {!isCollapsed && (
                <Flex justify="space-between" align="center" w="full" ml={4}>
                  <Text>Relatórios</Text>
                  <Icon
                    as={reportMenuOpen ? AiOutlineDown : AiOutlineRight}
                    fontSize="xs"
                  />
                </Flex>
              )}
            </Flex>

            <Collapse in={reportMenuOpen && !isCollapsed} animateOpacity>
              <VStack align="stretch" pl={4} spacing={0} mt={1}>
                <Link
                  as={RouterLink}
                  to="/reports?tab=overview"
                  _hover={{ textDecoration: "none", bg: "gray.700" }}
                  display="flex"
                  alignItems="center"
                  position="relative"
                  h="48px"
                  px={4}
                  borderRadius="md"
                  bg={location.pathname === "/reports" && currentTab === "overview" ? "gray.700" : "transparent"}
                  color={location.pathname === "/reports" && currentTab === "overview" ? "secondary" : "white"}
                  transition="all 0.2s"
                >
                  <Box
                    position="absolute"
                    left="0"
                    h="80%"
                    w="2px"
                    bg="secondary"
                    borderRadius="full"
                    opacity={location.pathname === "/reports" && currentTab === "overview" ? 1 : 0}
                    transition="opacity 0.2s ease-in-out"
                    top="50%"
                    transform="translateY(-50%)"
                  />
                  <Icon fontSize="18px" as={AiOutlineEye} />
                  <Text ml={3}>Visão geral</Text>
                </Link>
                <Link
                  as={RouterLink}
                  to="/reports?tab=analytics"
                  _hover={{ textDecoration: "none", bg: "gray.700" }}
                  display="flex"
                  alignItems="center"
                  position="relative"
                  h="48px"
                  px={4}
                  borderRadius="md"
                  bg={location.pathname === "/reports" && currentTab === "analytics" ? "gray.700" : "transparent"}
                  color={location.pathname === "/reports" && currentTab === "analytics" ? "secondary" : "white"}
                  transition="all 0.2s"
                >
                  <Box
                    position="absolute"
                    left="0"
                    h="80%"
                    w="2px"
                    bg="secondary"
                    borderRadius="full"
                    opacity={location.pathname === "/reports" && currentTab === "analytics" ? 1 : 0}
                    transition="opacity 0.2s ease-in-out"
                    top="50%"
                    transform="translateY(-50%)"
                  />
                  <Icon fontSize="18px" as={AiOutlineLineChart} />
                  <Text ml={3}>Análise</Text>
                </Link>
                <Link
                  as={RouterLink}
                  to="/reports?tab=year"
                  _hover={{ textDecoration: "none", bg: "gray.700" }}
                  display="flex"
                  alignItems="center"
                  position="relative"
                  h="48px"
                  px={4}
                  borderRadius="md"
                  bg={location.pathname === "/reports" && currentTab === "year" ? "gray.700" : "transparent"}
                  color={location.pathname === "/reports" && currentTab === "year" ? "secondary" : "white"}
                  transition="all 0.2s"
                >
                  <Box
                    position="absolute"
                    left="0"
                    h="80%"
                    w="2px"
                    bg="secondary"
                    borderRadius="full"
                    opacity={location.pathname === "/reports" && currentTab === "year" ? 1 : 0}
                    transition="opacity 0.2s ease-in-out"
                    top="50%"
                    transform="translateY(-50%)"
                  />
                  <Icon fontSize="18px" as={AiOutlineCalendar} />
                  <Text ml={3}>Ausências do ano</Text>
                </Link>
              </VStack>
            </Collapse>
          </Box>
        )}

        {isAdmin && (
          <Box>
            <Flex
              align="center"
              h="48px"
              px={4}
              borderRadius="md"
              cursor="pointer"
              _hover={{ bg: "gray.600" }}
              color="white"
              onClick={() => {
                if (!isCollapsed) setControlsMenuOpen(!controlsMenuOpen);
              }}
              justifyContent={isCollapsed ? "center" : "flex-start"}
            >
              <Icon fontSize="20px" as={AiOutlineSetting} />
              {!isCollapsed && (
                <Flex justify="space-between" align="center" w="full" ml={4}>
                  <Text>Controles</Text>
                  <Icon
                    as={controlsMenuOpen ? AiOutlineDown : AiOutlineRight}
                    fontSize="xs"
                  />
                </Flex>
              )}
            </Flex>

            <Collapse in={controlsMenuOpen && !isCollapsed} animateOpacity>
              <VStack align="stretch" pl={4} spacing={0} mt={1}>
                <Link
                  as={RouterLink}
                  to="/usuarios"
                  _hover={{ textDecoration: "none", bg: "gray.700" }}
                  display="flex"
                  alignItems="center"
                  position="relative"
                  h="48px"
                  px={4}
                  borderRadius="md"
                  bg={location.pathname === "/usuarios" ? "gray.700" : "transparent"}
                  color={location.pathname === "/usuarios" ? "secondary" : "white"}
                  transition="all 0.2s"
                >
                  <Box
                    position="absolute"
                    left="0"
                    h="80%"
                    w="2px"
                    bg="secondary"
                    borderRadius="full"
                    opacity={location.pathname === "/usuarios" ? 1 : 0}
                    transition="opacity 0.2s ease-in-out"
                    top="50%"
                    transform="translateY(-50%)"
                  />
                  <Icon fontSize="18px" as={AiOutlineTeam} />
                  <Text ml={3}>Usuários</Text>
                </Link>
                <Link
                  as={RouterLink}
                  to="/categorias"
                  _hover={{ textDecoration: "none", bg: "gray.700" }}
                  display="flex"
                  alignItems="center"
                  position="relative"
                  h="48px"
                  px={4}
                  borderRadius="md"
                  bg={location.pathname === "/categorias" ? "gray.700" : "transparent"}
                  color={location.pathname === "/categorias" ? "secondary" : "white"}
                  transition="all 0.2s"
                >
                  <Box
                    position="absolute"
                    left="0"
                    h="80%"
                    w="2px"
                    bg="secondary"
                    borderRadius="full"
                    opacity={location.pathname === "/categorias" ? 1 : 0}
                    transition="opacity 0.2s ease-in-out"
                    top="50%"
                    transform="translateY(-50%)"
                  />
                  <Icon fontSize="18px" as={AiOutlineTags} />
                  <Text ml={3}>Categorias</Text>
                </Link>
              </VStack>
            </Collapse>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default Sidebar;
